import { strToU8, unzipSync, type Unzipped } from 'fflate';
import { fail, SongPackError } from './SongPackError.js';

/**
 * ZIP container access for Song Pack v1, with structural validation performed
 * *before* anything is inflated.
 *
 * This layer is deliberately ignorant of Song Pack semantics: it knows about ZIP
 * entries, paths and sizes, not about manifests or charts. Semantic rules live in
 * `SongPackValidator`.
 *
 * Reading happens in two passes:
 *
 *   1. `open()` walks the central directory with a filter that extracts nothing.
 *      Names, declared sizes and compression methods are checked here, so a
 *      malformed or hostile archive is rejected without spending a byte of
 *      inflate work. This matters concretely: for deflated entries fflate
 *      pre-allocates the *declared* uncompressed size, so a lying size field is a
 *      memory-exhaustion vector unless it is rejected before extraction.
 *   2. `extract()` inflates only the named entries, then re-checks the produced
 *      sizes because central-directory sizes are attacker-controlled hints.
 */

/** Stored, i.e. no compression. */
const COMPRESSION_STORE = 0;
/** Deflate. `System.IO.Compression.ZipArchive` and Python's `zipfile` both use this. */
const COMPRESSION_DEFLATE = 8;

/** Exactly the set fflate's `unzipSync` can decode; anything else it would throw on. */
const SUPPORTED_COMPRESSION: readonly number[] = [COMPRESSION_STORE, COMPRESSION_DEFLATE];

const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;
const DRIVE_QUALIFIED = /^[A-Za-z]:/;

export interface ArchiveLimits {
  /** Hard cap on entry count. A version 1 pack holds six files. */
  readonly maxEntries: number;
  /** Cap on any single entry, uncompressed. */
  readonly maxEntryBytes: number;
  /** Cap on the sum of all uncompressed entries. */
  readonly maxTotalBytes: number;
}

/**
 * Defaults sized from Unity's own per-entry limits in `SongPackService.Read`
 * (512 MiB music, 32 MiB cover, 16 MiB per chart, 1 MiB manifest) plus headroom
 * for a bundled music video, which is the one asset that legitimately reaches
 * hundreds of megabytes.
 *
 * `maxTotalBytes` is a zip-bomb guard, and it is worth being precise about what
 * it does now that it is over a gigabyte: it is checked against *declared*
 * sizes, before anything is inflated, and the real protection is that `extract`
 * only ever inflates the entries a caller names. A pack claiming 1.2 GB is
 * refused outright; a pack claiming 900 MB still only costs whatever the caller
 * actually asks for.
 *
 * `maxEntries` is generous enough to survive a producer adding a file, but small
 * enough that a directory with a million entries is rejected immediately.
 */
export const DEFAULT_ARCHIVE_LIMITS: ArchiveLimits = Object.freeze({
  maxEntries: 64,
  maxEntryBytes: 512 * 1024 * 1024,
  maxTotalBytes: 1152 * 1024 * 1024,
});

export interface ArchiveEntryInfo {
  /** Entry name exactly as stored. Always a bare file name at the archive root. */
  readonly name: string;
  /** Uncompressed size declared by the central directory. */
  readonly declaredSize: number;
  /** Compressed size declared by the central directory. */
  readonly compressedSize: number;
  readonly compression: number;
}

/** Where a stored entry's bytes actually sit in the archive. */
export interface StoredRange {
  readonly start: number;
  readonly end: number;
}

export class SongPackArchive {
  /** Keyed by lower-cased name, matching Unity's `OrdinalIgnoreCase` dictionary. */
  private readonly index: ReadonlyMap<string, ArchiveEntryInfo>;
  private readonly data: Uint8Array;
  private readonly limits: ArchiveLimits;

  private constructor(
    data: Uint8Array,
    index: ReadonlyMap<string, ArchiveEntryInfo>,
    limits: ArchiveLimits,
  ) {
    this.data = data;
    this.index = index;
    this.limits = limits;
  }

  /**
   * Byte range of a **stored** entry's payload, or null.
   *
   * This exists for one asset: a bundled music video. An MP4 is already
   * compressed, so it is written with no deflate, which means its bytes are in
   * the archive verbatim and can be handed to a `Blob` by reference instead of
   * inflated into one. The difference at 200 MB is not academic — going through
   * `extract` costs an inflate buffer plus a copy, and measured at 658 MB of
   * heap for a 227 MB pack before this existed.
   *
   * Returns null for deflated entries, for anything not in the archive, and for
   * a range that does not fit inside it. Callers fall back to `extract`.
   */
  storedRange(name: string): StoredRange | null {
    const entry = this.index.get(name.toLowerCase());
    if (entry === undefined || entry.compression !== COMPRESSION_STORE) return null;
    const start = findLocalPayloadOffset(this.data, entry.name);
    if (start === null) return null;
    const end = start + entry.declaredSize;
    return end <= this.data.length ? { start, end } : null;
  }

  /**
   * Validates the archive structure and returns a handle. Nothing is inflated.
   *
   * @throws SongPackError on any structural violation.
   */
  static open(data: Uint8Array, limits: Partial<ArchiveLimits> = {}): SongPackArchive {
    const resolved: ArchiveLimits = { ...DEFAULT_ARCHIVE_LIMITS, ...limits };
    const listing = readCentralDirectory(data);
    const index = new Map<string, ArchiveEntryInfo>();
    let total = 0;
    let seen = 0;

    for (const entry of listing) {
      // Directory records carry no payload. Unity skips them via the empty
      // `ZipArchiveEntry.Name` check; the ZIP spec marks them with a trailing '/'.
      if (entry.name.length === 0 || entry.name.endsWith('/')) continue;

      seen += 1;
      if (seen > resolved.maxEntries) {
        fail('TOO_MANY_ENTRIES', `The archive declares more than ${resolved.maxEntries} files.`);
      }

      assertRootFileName(entry.name);

      if (!SUPPORTED_COMPRESSION.includes(entry.compression)) {
        fail(
          'UNSUPPORTED_COMPRESSION',
          `Unsupported compression method ${entry.compression}; only stored and deflate are allowed.`,
          { entry: entry.name },
        );
      }

      if (!Number.isFinite(entry.declaredSize) || entry.declaredSize < 0) {
        fail('ENTRY_TOO_LARGE', 'The archive declares an invalid uncompressed size.', {
          entry: entry.name,
        });
      }
      if (entry.declaredSize > resolved.maxEntryBytes) {
        fail('ENTRY_TOO_LARGE', `Entry is larger than the ${resolved.maxEntryBytes} byte limit.`, {
          entry: entry.name,
        });
      }

      total += entry.declaredSize;
      if (total > resolved.maxTotalBytes) {
        fail(
          'ARCHIVE_TOO_LARGE',
          `Uncompressed archive content exceeds the ${resolved.maxTotalBytes} byte limit.`,
          { entry: entry.name },
        );
      }

      const key = entry.name.toLowerCase();
      if (index.has(key)) {
        fail('DUPLICATE_ENTRY', `Duplicate Song Pack entry: ${entry.name}`, { entry: entry.name });
      }
      index.set(key, entry);
    }

    return new SongPackArchive(data, index, resolved);
  }

  /** Entry metadata, keyed by lower-cased name. */
  get entries(): ReadonlyMap<string, ArchiveEntryInfo> {
    return this.index;
  }

  has(name: string): boolean {
    return this.index.has(name.toLowerCase());
  }

  info(name: string): ArchiveEntryInfo | undefined {
    return this.index.get(name.toLowerCase());
  }

  /**
   * Inflates the requested entries in a single pass.
   *
   * @param names Entry names to read. A missing name is an error, not a skip.
   * @param sizeLimits Optional per-entry byte caps, used to reproduce Unity's
   *   distinct limits for manifest, music, cover and chart entries.
   * @throws SongPackError when an entry is missing or exceeds its cap.
   */
  extract(
    names: readonly string[],
    sizeLimits: Readonly<Record<string, number>> = {},
  ): Map<string, Uint8Array> {
    /** stored entry name -> name as requested by the caller */
    const wanted = new Map<string, string>();
    for (const name of names) {
      const entry = this.index.get(name.toLowerCase());
      if (entry === undefined) {
        fail('MISSING_ENTRY', `Missing Song Pack entry: ${name}`, { entry: name });
      }
      const cap = sizeLimits[name] ?? this.limits.maxEntryBytes;
      if (entry.declaredSize > cap) {
        fail('ENTRY_TOO_LARGE', `Song Pack entry is too large: ${name}`, { entry: name });
      }
      wanted.set(entry.name, name);
    }

    let unzipped: Unzipped;
    try {
      unzipped = unzipSync(this.data, { filter: (file) => wanted.has(file.name) });
    } catch (cause) {
      fail('INVALID_ARCHIVE', 'The archive could not be decompressed.', { cause });
    }

    const result = new Map<string, Uint8Array>();
    for (const [storedName, requestedName] of wanted) {
      const bytes = unzipped[storedName];
      if (bytes === undefined) {
        fail('MISSING_ENTRY', `Missing Song Pack entry: ${requestedName}`, { entry: requestedName });
      }
      // The central directory is attacker-controlled, so re-check the real size.
      const cap = sizeLimits[requestedName] ?? this.limits.maxEntryBytes;
      if (bytes.length > cap) {
        fail('ENTRY_TOO_LARGE', `Song Pack entry exceeded its limit: ${requestedName}`, {
          entry: requestedName,
        });
      }
      result.set(requestedName, bytes);
    }
    return result;
  }
}

/**
 * Lists every central-directory record without inflating anything.
 *
 * `unzipSync` is used purely as a ZIP parser here: the filter always returns
 * false, so no entry is decompressed and no output buffer is allocated.
 */
function readCentralDirectory(data: Uint8Array): ArchiveEntryInfo[] {
  const listing: ArchiveEntryInfo[] = [];
  try {
    unzipSync(data, {
      filter: (file) => {
        listing.push({
          name: file.name,
          declaredSize: file.originalSize,
          compressedSize: file.size,
          compression: file.compression,
        });
        return false;
      },
    });
  } catch (cause) {
    if (cause instanceof SongPackError) throw cause;
    fail('INVALID_ARCHIVE', 'The file is not a readable ZIP archive.', { cause });
  }
  return listing;
}

/** Local file header signature, `PK\x03\x04`. */
const LOCAL_HEADER_SIGNATURE = 0x04034b50;
const CENTRAL_HEADER_SIGNATURE = 0x02014b50;
const END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50;
/** Sentinel meaning "the real value lives in a ZIP64 record". */
const ZIP64_SENTINEL = 0xffffffff;

/**
 * Finds where a named entry's payload starts, through the central directory.
 *
 * Walking local headers instead would be simpler, and does not work. A
 * streaming zipper does not know an entry's compressed size when it writes the
 * header, so it sets the data-descriptor flag, leaves the size as zero and puts
 * the real value *after* the payload — which means the next header cannot be
 * found by arithmetic. fflate's `Zip`, the writer on the other side of this
 * module, does exactly that. The central directory records every entry's offset,
 * which is what it is for, and is unaffected.
 *
 * Deliberately conservative. An unexpected signature, a ZIP64 sentinel, a name
 * that does not match, a range outside the file: all return null, and the caller
 * inflates through the normal path instead. Being wrong here would hand a
 * `Blob` somebody else's bytes, which is far worse than being slow.
 */
function findLocalPayloadOffset(data: Uint8Array, name: string): number | null {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  // fflate's encoder rather than TextEncoder: this layer has no DOM and no Node
  // globals, which is the rule that keeps the format readable from anywhere.
  const wanted = strToU8(name);

  const directory = findCentralDirectoryStart(view, data.length);
  if (directory === null) return null;

  let offset = directory;
  while (offset + 46 <= data.length && view.getUint32(offset, true) === CENTRAL_HEADER_SIGNATURE) {
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localHeader = view.getUint32(offset + 42, true);
    const nameStart = offset + 46;
    if (nameStart + nameLength > data.length) return null;

    let matches = nameLength === wanted.length;
    for (let index = 0; matches && index < nameLength; index += 1) {
      if (data[nameStart + index] !== wanted[index]) matches = false;
    }
    if (matches) {
      if (localHeader === ZIP64_SENTINEL || localHeader + 30 > data.length) return null;
      if (view.getUint32(localHeader, true) !== LOCAL_HEADER_SIGNATURE) return null;
      // Read from the *local* header: its name and extra fields are permitted to
      // differ in length from the central directory's copy of them.
      const payload =
        localHeader +
        30 +
        view.getUint16(localHeader + 26, true) +
        view.getUint16(localHeader + 28, true);
      return payload <= data.length ? payload : null;
    }
    offset = nameStart + nameLength + extraLength + commentLength;
  }
  return null;
}

/**
 * Locates the central directory through the end-of-central-directory record.
 *
 * Scanned backwards because that record sits at the very end of the file, after
 * an optional comment of up to 64 KiB. Nothing earlier than that is searched.
 */
function findCentralDirectoryStart(view: DataView, length: number): number | null {
  const earliest = Math.max(0, length - (22 + 0xffff));
  for (let offset = length - 22; offset >= earliest; offset -= 1) {
    if (view.getUint32(offset, true) !== END_OF_CENTRAL_DIRECTORY_SIGNATURE) continue;
    const start = view.getUint32(offset + 16, true);
    return start === ZIP64_SENTINEL || start >= length ? null : start;
  }
  return null;
}
/**
 * Enforces that an entry is a bare file name at the archive root.
 *
 * Unity expresses this as `entry.Name != entry.FullName || entry.FullName.Contains("..")`.
 * Rejecting any separator already blocks ZIP Slip, but the explicit `..` test is
 * kept so a bare `..` name — which contains no separator — is rejected too.
 */
function assertRootFileName(name: string): void {
  if (name.includes('/') || name.includes('\\')) {
    fail('ENTRY_NOT_AT_ROOT', `Song Pack files must be at the ZIP root: ${name}`, { entry: name });
  }
  if (name.includes('..')) {
    fail('ENTRY_PATH_TRAVERSAL', `Song Pack entry name may not contain '..': ${name}`, {
      entry: name,
    });
  }
  // Drive-qualified and control-character names cannot occur in a well-formed
  // archive produced by either Unity or Python, and indicate tampering.
  if (DRIVE_QUALIFIED.test(name)) {
    fail('ENTRY_NOT_AT_ROOT', `Song Pack entry name may not be drive-qualified: ${name}`, {
      entry: name,
    });
  }
  if (CONTROL_CHARACTERS.test(name)) {
    fail('ENTRY_NOT_AT_ROOT', 'Song Pack entry name contains a control character.', { entry: name });
  }
  if (name.trim().length === 0) {
    fail('ENTRY_NOT_AT_ROOT', 'Song Pack entry name is blank.', { entry: name });
  }
}
